import React from 'react';
import PropTypes from 'prop-types';

/**
 * Avatar component for displaying user profile images or initials
 * Supports multiple sizes and color variants
 * - Default: shows up to 3 characters from initials
 * - shortName: shows only 1 character
 * - src: shows image from URL
 */
// Derive initials from a full name ("Nguyen Van A" -> "NA").
// A single token is kept up to 3 characters: most single-token values in the app
// are 3-letter employee codes ("THV"), and clipping them to 2 dropped a letter.
const nameToInitials = (name) => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 3);
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
};

const Avatar = ({
  src,
  alt = 'Avatar',
  initials = '',
  name = '',
  size = 'md',
  variant = 'default',
  className = '',
  onClick = null,
  shortName = false,
  silhouette = false,
}) => {
  const sizeClasses = {
    xs: 'tw-w-6 tw-h-6 tw-text-xs',
    sm: 'tw-w-8 tw-h-8 tw-text-sm',
    m: 'tw-w-10 tw-h-10',
    md: 'tw-w-10 tw-h-10',
    lg: 'tw-w-12 tw-h-12',
    xl: 'tw-w-16 tw-h-16',
    '2xl': 'tw-w-20 tw-h-20',
    '3xl': 'tw-w-[68px] tw-h-[68px]',
    '4xl': 'tw-w-32 tw-h-32',
  };

  // Unknown/legacy sizes degrade to md instead of rendering an "undefined" class
  const resolvedSize = sizeClasses[size] ? size : 'md';

  // Text size based on avatar size and character count
  const getTextSizeClass = () => {
    if (shortName) {
      // Single character - larger text
      return {
        xs: 'tw-text-xs',
        sm: 'tw-text-xs',
        m: 'tw-text-base',
        md: 'tw-text-base',
        lg: 'tw-text-lg',
        xl: 'tw-text-2xl',
        '2xl': 'tw-text-3xl',
        '3xl': 'tw-text-3xl',
        '4xl': 'tw-text-4xl',
      }[resolvedSize];
    } else {
      // Multiple characters - smaller text to fit
      return {
        xs: 'tw-text-xs',
        sm: 'tw-text-xs',
        m: 'tw-text-sm',
        md: 'tw-text-sm',
        lg: 'tw-text-base',
        xl: 'tw-text-lg',
        '2xl': 'tw-text-xl',
        '3xl': 'tw-text-2xl',
        '4xl': 'tw-text-3xl',
      }[resolvedSize];
    }
  };

  const variantClasses = {
    default: 'tw-bg-slate-200 tw-text-slate-700',
    primary: 'tw-bg-blue-500 tw-text-white',
    success: 'tw-bg-green-500 tw-text-white',
    warning: 'tw-bg-orange-500 tw-text-white',
    danger: 'tw-bg-red-500 tw-text-white',
    info: 'tw-bg-primary-500 tw-text-white',
  };

  const cursorClass = onClick ? 'tw-cursor-pointer' : '';

  // If image source provided, show image avatar
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={`
          ${sizeClasses[resolvedSize]}
          tw-rounded-xl
          tw-object-cover
          tw-border-2 tw-border-white
          tw-shadow-md
          ${cursorClass}
          ${className}
        `}
        onClick={onClick}
      />
    );
  }

  // Determine character limit based on shortName prop
  const charLimit = shortName ? 1 : 3;
  const sourceInitials = initials || nameToInitials(name);
  const displayText = sourceInitials.substring(0, charLimit).toUpperCase();

  // Silhouette avatar (generic person glyph on a soft gray circle) — used when
  // explicitly requested, or as the fallback when there is nothing to derive
  // initials from. Head + shoulders anchored to the bottom edge, like the
  // classic placeholder avatar.
  if (silhouette || !displayText) {
    return (
      <div
        className={`
          ${sizeClasses[resolvedSize]}
          tw-bg-gradient-to-b tw-from-slate-200 tw-to-slate-300
          tw-rounded-xl
          tw-flex tw-items-end tw-justify-center
          tw-overflow-hidden
          tw-border-2 tw-border-white
          tw-shadow-md
          ${cursorClass}
          ${className}
        `}
        title={alt !== 'Avatar' ? alt : undefined}
        onClick={onClick}
      >
        <svg viewBox="0 0 24 24" className="tw-w-[68%] tw-h-[68%] tw-fill-white" aria-hidden="true">
          <circle cx="12" cy="8.2" r="4.2" />
          <path d="M12 13.6c-4.6 0-8.2 2.6-8.2 6.4v4h16.4v-4c0-3.8-3.6-6.4-8.2-6.4z" />
        </svg>
      </div>
    );
  }

  // Show initials avatar
  return (
    <div
      className={`
        ${sizeClasses[resolvedSize]}
        ${variantClasses[variant]}
        tw-rounded-xl
        tw-flex tw-items-center tw-justify-center
        tw-font-bold
        tw-user-select-none
        tw-border-2 tw-border-white
        tw-shadow-md
        ${getTextSizeClass()}
        ${cursorClass}
        ${className}
      `}
      style={{ lineHeight: '1' }}
      onClick={onClick}
    >
      {displayText}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  initials: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl']),
  variant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'danger', 'info']),
  className: PropTypes.string,
  onClick: PropTypes.func,
  shortName: PropTypes.bool,
  silhouette: PropTypes.bool,
};

/**
 * AvatarGroup — lays out a row of overlapping avatars.
 * - dense: tighter overlap (used in grid cells)
 */
export const AvatarGroup = ({ children, dense = false, className = '' }) => (
  <div
    className={`tw-flex tw-items-center ${dense ? '-tw-space-x-2' : '-tw-space-x-1'} ${className}`}
  >
    {children}
  </div>
);

AvatarGroup.propTypes = {
  children: PropTypes.node,
  dense: PropTypes.bool,
  className: PropTypes.string,
};

export default Avatar;
